import React, { useCallback } from 'react';
import { Panel, Group, Separator } from 'react-resizable-panels';
import { ToolId, ToolConfig } from '../workspace-config';
import { GlassPanel, Spinner } from '../../../components/common';
import { PanelToolPicker } from './PanelToolPicker';

interface SplitLayoutProps {
    /** The tool ID rendered in the left panel (the user's active tool) */
    leftToolId: ToolId;
    /** The tool ID rendered in the right panel, or null to show the picker */
    rightToolId: ToolId | null;
    /** Lazy factory that returns a React element for a given tool ID */
    getToolComponent: (toolId: ToolId) => React.ReactNode;
    /** Currently focused panel side */
    focusedSide: 'left' | 'right';
    /** Callback when a panel gains focus */
    onPanelFocus: (side: 'left' | 'right') => void;
    /** Called when user selects a tool in the right-panel picker */
    onRightToolSelect: (toolId: ToolId) => void;
    /** Available tools to show in the picker (workspace-filtered) */
    availableTools: ToolConfig[];
    /** Stable key for persisting panel sizes to localStorage */
    persistKey?: string;
}

/** localStorage helpers — Layout is { [panelId]: number } */
const STORAGE_PREFIX = 'onbook-split-sizes:';
type Layout = { [id: string]: number };

function isLayout(value: unknown): value is Layout {
    if (typeof value !== 'object' || value === null) {
        return false;
    }

    return Object.values(value).every(item => typeof item === 'number');
}

function readPersistedSizes(key: string): Layout | null {
    try {
        const raw = localStorage.getItem(`${STORAGE_PREFIX}${key}`);
        if (!raw) return null;
        const parsed: unknown = JSON.parse(raw);
        return isLayout(parsed) ? parsed : null;
    } catch {
        return null;
    }
}

function persistSizes(key: string, layout: Layout) {
    try {
        localStorage.setItem(`${STORAGE_PREFIX}${key}`, JSON.stringify(layout));
    } catch { /* quota exceeded — silently ignore */ }
}

/**
 * SplitLayout — renders a two-panel side-by-side layout.
 *
 * Left panel: the user's active tool (always rendered).
 * Right panel: either the selected companion tool, or a PanelToolPicker
 * if no companion has been chosen yet.
 */
export const SplitLayout: React.FC<SplitLayoutProps> = ({
    leftToolId,
    rightToolId,
    getToolComponent,
    focusedSide,
    onPanelFocus,
    onRightToolSelect,
    availableTools,
    persistKey,
}) => {
    const sizeKey = persistKey && rightToolId
        ? `${persistKey}:${leftToolId}:${rightToolId}`
        : null;
    const persisted = sizeKey ? readPersistedSizes(sizeKey) : null;

    const handleLayoutChanged = useCallback((layout: Layout) => {
        if (sizeKey) {
            persistSizes(sizeKey, layout);
        }
    }, [sizeKey]);

    const leftComponent = getToolComponent(leftToolId);
    const rightComponent = rightToolId ? getToolComponent(rightToolId) : null;
    const panelFallback = (
        <div className="flex h-64 items-center justify-center">
            <Spinner size="lg" />
        </div>
    );

    return (
        <Group
            orientation="horizontal"
            onLayoutChanged={handleLayoutChanged}
        >
            {/* Left Panel — always the active tool */}
            <Panel
                id="split-left"
                defaultSize={persisted?.['split-left'] ?? 60}
                minSize={25}
            >
                <GlassPanel
                    ariaLabel="Left tool panel"
                    focused={focusedSide === 'left'}
                    onPanelFocus={() => onPanelFocus('left')}
                    className="h-full"
                >
                    <React.Suspense fallback={panelFallback}>
                        {leftComponent}
                    </React.Suspense>
                </GlassPanel>
            </Panel>

            <Separator className="w-1 hover:w-1.5 bg-gray-200 dark:bg-gray-700 hover:bg-primary/30 dark:hover:bg-primary/40 transition-all cursor-col-resize" />

            {/* Right Panel — companion tool or picker */}
            <Panel
                id="split-right"
                defaultSize={persisted?.['split-right'] ?? 40}
                minSize={20}
            >
                <GlassPanel
                    ariaLabel="Right tool panel"
                    focused={focusedSide === 'right'}
                    onPanelFocus={() => onPanelFocus('right')}
                    className="h-full"
                >
                    {rightComponent ? (
                        <React.Suspense fallback={panelFallback}>
                            {rightComponent}
                        </React.Suspense>
                    ) : (
                        <PanelToolPicker
                            tools={availableTools}
                            excludeToolId={leftToolId}
                            onSelect={onRightToolSelect}
                        />
                    )}
                </GlassPanel>
            </Panel>
        </Group>
    );
};
